import { WORLD_GEN_VERSION } from "../constants";
import type { SphereConfig } from "./areas";
import type { GlobalLayer } from "./generate";
import { sphereKey } from "./stream";

/**
 * A sphere's global layer as data (see stream_plan.md, "Startup"): made
 * once where the sphere lives (its Sphere DO on Cloudflare) and handed to
 * every region and client of it, which adopt it (see adoptGlobal) instead of
 * making it themselves. Making it is the slow part of starting up (the
 * stream's track and its rocks); reading it back is a few milliseconds. It's
 * only ever a copy: the seed makes the same layer every time, so a region
 * that can't get it just makes it.
 */

/** What a sphere's layout is kept and fetched by: the sphere, and the generator that made it (a new one makes it afresh). */
export function layoutKey(sphere: Pick<SphereConfig, "seed" | "radius" | "areaSize">): string {
  return `${sphereKey(sphere).replace(/:/g, "_")}-v${WORLD_GEN_VERSION}`;
}

/** A global layer as text (JSON; its maps and sets as lists). */
export function encodeLayout(g: GlobalLayer): string {
  return JSON.stringify({
    v: WORLD_GEN_VERSION,
    suns: g.suns,
    stream: { ...g.stream, turns: [...g.stream.turns] },
    streamRocks: g.streamRocks,
    reaches: [...g.reaches],
    rings: g.rings,
    systems: [...g.systems],
  });
}

/** A global layer back from encodeLayout, for `sphere`. Throws if it isn't one, or is for another sphere or generator. */
export function decodeLayout(sphere: Pick<SphereConfig, "seed" | "radius" | "areaSize">, text: string): GlobalLayer {
  const d = JSON.parse(text) as Omit<GlobalLayer, "reaches" | "systems" | "stream"> & {
    v: number;
    stream: Omit<GlobalLayer["stream"], "turns"> & { turns: [string, 1 | -1][] };
    reaches: string[];
    systems: [string, GlobalLayer["systems"] extends Map<string, infer V> ? V : never][];
  };
  if (d.v !== WORLD_GEN_VERSION) throw new Error(`layout from generator ${d.v}, not ${WORLD_GEN_VERSION}`);
  if (d.stream.key !== sphereKey(sphere)) throw new Error(`layout of sphere ${d.stream.key}, not ${sphereKey(sphere)}`);
  return {
    suns: d.suns,
    stream: { ...d.stream, turns: new Map(d.stream.turns) },
    streamRocks: d.streamRocks,
    reaches: new Set(d.reaches),
    rings: d.rings,
    systems: new Map(d.systems),
  };
}
